import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';
import { asConfirmation, type SetupConfirmation } from '../../core/setupStateApi';
import type { Thresholds } from '../../core/thresholds';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type CriteriaCondition = { measure: string; minimum: number };
export type CriteriaRuleSet = { outcome: string; conditions: CriteriaCondition[]; displayNote?: string };
export type CriteriaGradeBand = {
  assessment: string;
  grades: string[] | 'all';
  noExit: boolean;
  ruleSets: CriteriaRuleSet[];
};

export type ExitCriteria = {
  stateName: string;
  source: 'state_record' | 'district' | null;
  gradeBands: CriteriaGradeBand[];
  exitCriteriaConfirmed: SetupConfirmation | null;
  yearsConfirmed: SetupConfirmation | null;
};

export type SettingRow = { label: string; value: string; note?: string };

type RawBand = {
  assessment?: unknown;
  grades?: unknown;
  noExit?: unknown;
  ruleSets?: unknown;
};

type ExitCriteriaBody = {
  state?: unknown;
  stateName?: unknown;
  effective?: { source?: unknown; gradeBands?: unknown } | null;
  exitCriteriaConfirmed?: { at?: unknown; by?: unknown } | null;
  yearsConfirmed?: { at?: unknown; by?: unknown } | null;
} | null;

const GRADE_ORDER = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const MEASURE_NAMES: Record<string, string> = {
  overall: 'Overall composite',
  literacy: 'Literacy composite',
  oral: 'Oral composite',
  comprehension: 'Comprehension composite',
  listening: 'Listening',
  speaking: 'Speaking',
  reading: 'Reading',
  writing: 'Writing',
};

const ALTERNATE_MEASURE_NAMES: Record<string, string> = { ...MEASURE_NAMES, overall: 'Overall' };

const OUTCOME_LABELS: Record<string, string> = {
  exits: 'Exits',
  eligible_for_review: 'Eligible for review',
};

function conditionOf(raw: unknown): CriteriaCondition | null {
  const c = raw as { measure?: unknown; minimum?: unknown } | null;
  if (!c || typeof c.measure !== 'string' || typeof c.minimum !== 'number') return null;
  return { measure: c.measure, minimum: c.minimum };
}

function ruleSetOf(raw: unknown): CriteriaRuleSet | null {
  const r = raw as { outcome?: unknown; conditions?: unknown; displayNote?: unknown } | null;
  if (!r || typeof r.outcome !== 'string') return null;
  const conditions = Array.isArray(r.conditions) ? r.conditions.map(conditionOf) : [];
  return {
    outcome: r.outcome,
    conditions: conditions.filter((c): c is CriteriaCondition => c !== null),
    ...(typeof r.displayNote === 'string' && r.displayNote !== '' ? { displayNote: r.displayNote } : {}),
  };
}

function gradesOf(raw: unknown): string[] | 'all' {
  if (!Array.isArray(raw)) return 'all';
  const grades = raw.filter((g): g is string => typeof g === 'string');
  return grades.length === 0 ? 'all' : grades;
}

function bandOf(raw: RawBand): CriteriaGradeBand {
  const ruleSets = Array.isArray(raw.ruleSets) ? raw.ruleSets.map(ruleSetOf) : [];
  return {
    assessment: typeof raw.assessment === 'string' ? raw.assessment : 'access',
    grades: gradesOf(raw.grades),
    noExit: raw.noExit === true,
    ruleSets: ruleSets.filter((r): r is CriteriaRuleSet => r !== null),
  };
}

export async function fetchExitCriteria(): Promise<ExitCriteria> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/settings/exit-criteria`, { headers: authHeaders(token) });
  if (!response.ok) {
    throw new Error(`exit-criteria failed: ${response.status}`);
  }
  const body = (await response.json()) as ExitCriteriaBody;
  const effective = body?.effective;
  const source = effective?.source;
  const bands = Array.isArray(effective?.gradeBands) ? (effective.gradeBands as RawBand[]) : [];
  return {
    stateName:
      typeof body?.stateName === 'string' && body.stateName !== ''
        ? body.stateName
        : typeof body?.state === 'string'
          ? body.state
          : '',
    source: source === 'state_record' || source === 'district' ? source : null,
    gradeBands: bands.map(bandOf),
    exitCriteriaConfirmed: asConfirmation(body?.exitCriteriaConfirmed),
    yearsConfirmed: asConfirmation(body?.yearsConfirmed),
  };
}

export function criteriaHeading(criteria: Pick<ExitCriteria, 'stateName' | 'source'>): string {
  if (criteria.source === 'district' || criteria.stateName === '') return 'Your district’s exit criteria';
  return `${criteria.stateName}’s exit criteria`;
}

function gradeRanges(grades: string[]): string {
  const ordered = GRADE_ORDER.filter((g) => grades.includes(g));
  const ranges: string[] = [];
  let first: string | null = null;
  let last: string | null = null;
  const close = () => {
    if (first !== null && last !== null) ranges.push(first === last ? first : `${first}–${last}`);
  };
  for (const grade of ordered) {
    if (first !== null && last !== null && GRADE_ORDER.indexOf(grade) === GRADE_ORDER.indexOf(last) + 1) {
      last = grade;
      continue;
    }
    close();
    first = grade;
    last = grade;
  }
  close();
  return ranges.join(', ');
}

export function gradesLabel(grades: string[] | 'all'): string {
  if (grades === 'all') return 'all grades';
  if (grades.length === 1) return grades[0] === 'K' ? 'kindergarten' : `grade ${grades[0]}`;
  return `grades ${gradeRanges(grades)}`;
}

function bandLabel(band: CriteriaGradeBand): string {
  if (band.assessment !== 'alternate_access') return gradesLabel(band.grades);
  return band.grades === 'all' ? 'alternate ACCESS' : `alternate ACCESS, ${gradesLabel(band.grades)}`;
}

function formatMinimum(band: CriteriaGradeBand, minimum: number): string {
  return band.assessment === 'alternate_access' ? String(Math.round(minimum)) : minimum.toFixed(1);
}

function measureName(band: CriteriaGradeBand, measure: string): string {
  const names = band.assessment === 'alternate_access' ? ALTERNATE_MEASURE_NAMES : MEASURE_NAMES;
  return names[measure] ?? measure;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function criteriaRows(bands: CriteriaGradeBand[]): SettingRow[] {
  return bands.flatMap((band) => {
    if (band.ruleSets.length === 0) {
      return band.noExit ? [{ label: capitalize(bandLabel(band)), value: 'No exit' }] : [];
    }
    return band.ruleSets.map((rule) => ({
      label: `${OUTCOME_LABELS[rule.outcome] ?? capitalize(rule.outcome)}, ${bandLabel(band)}`,
      value: rule.conditions
        .map((c) => `${measureName(band, c.measure)} ≥ ${formatMinimum(band, c.minimum)}`)
        .join(', '),
      ...(rule.displayNote ? { note: rule.displayNote } : {}),
    }));
  });
}

export function yearsRows(years: Pick<Thresholds, 'newcomerMaxYears' | 'ltelMinYears'>): SettingRow[] {
  return [
    { label: 'Newcomer', value: `First ${years.newcomerMaxYears} years as an EL` },
    { label: 'Long-term ML', value: `More than ${years.ltelMinYears - 1} years as an EL` },
  ];
}

export function confirmedLabel(confirmation: SetupConfirmation | null): string | null {
  if (confirmation === null) return null;
  const date = new Date(confirmation.at);
  if (Number.isNaN(date.getTime())) return null;
  return `Confirmed ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}
